import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowRight } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    { name: 'Print on Demand', href: '/services#print-on-demand' },
    { name: 'Dropshipping Fulfillment', href: '/services#dropshipping' },
    { name: 'Product Sourcing', href: '/services#product-sourcing' },
    { name: 'Global Warehousing', href: '/services#global-warehousing' },
    { name: 'Custom Branding', href: '/services#custom-branding' },
    { name: 'TikTok Shop Solutions', href: '/services#tiktok-shop' },
  ];

  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <footer data-testid="footer" className="bg-[#78350f] text-[#fef3c7] relative overflow-hidden">
      {/* Decorative organic shape */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#ea580c]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      
      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-16 relative">
        <div className="bg-[#ea580c] organic-shape-1 p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-6 warm-shadow-lg">
          <div>
            <h3 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-semibold text-white mb-2">
              Ready to Grow Together?
            </h3>
            <p className="text-orange-100">
              Partner with us for seamless fulfillment solutions.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="footer-cta-button"
            className="flex items-center gap-2 bg-white text-[#ea580c] px-8 py-4 font-semibold hover:bg-[#fef3c7] transition-all duration-300 whitespace-nowrap group organic-shape-3"
          >
            Get Started
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 border-t border-[#92400e] relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-white mb-6">
              Granitecurrent
            </h3>
            <p className="text-[#fcd9b8] text-sm mb-2">
              Granite Current LLC
            </p>
            <p className="text-[#fcd9b8] text-sm leading-relaxed">
              Your trusted partner for Print on Demand and ecommerce fulfillment solutions.
            </p>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-white mb-6">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.href}
                    data-testid={`footer-link-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-[#fcd9b8] hover:text-[#ea580c] text-sm transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-[#fcd9b8] hover:text-[#ea580c] text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="mailto:support@Granitecurrent.com"
                  data-testid="footer-email"
                  className="flex items-center gap-3 text-[#fcd9b8] hover:text-[#ea580c] text-sm transition-colors"
                >
                  <Mail size={16} className="text-[#ea580c]" />
                  support@Granitecurrent.com
                </a>
              </li>
              <li>
                <a
                  href="tel:+14804392436"
                  data-testid="footer-phone"
                  className="flex items-center gap-3 text-[#fcd9b8] hover:text-[#ea580c] text-sm transition-colors"
                >
                  <Phone size={16} className="text-[#ea580c]" />
                  +1 480 439 2436
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-[#fcd9b8] text-sm">
                  <MapPin size={16} className="text-[#ea580c] mt-0.5 flex-shrink-0" />
                  <span>2707 W Desert Ranch Rd,<br />Phoenix, AZ 85086</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#92400e]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-[#fcd9b8]">
            <p>© {currentYear} Granite Current LLC. All rights reserved.</p>
            <a
              href="https://Granitecurrent.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#ea580c] transition-colors"
            >
              Granitecurrent.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
